import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, CheckCircle, XCircle, Loader2, MessageSquare, FileText, Users, Shield, Award, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { markSlideViewed, markQuizCompleted, getActivityProgress } from '../utils/progressTracker';
import confetti from 'canvas-confetti';

const ACTIVITY_ID = 'deap-activite10';

const DEAPActivite10Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [showCongrats, setShowCongrats] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const savedProgress = getActivityProgress(ACTIVITY_ID);
    if (savedProgress && savedProgress.lastViewedSlide > 0) {
      // Resume from last position optionally
    }
  }, []);

  // Trigger confetti when quiz is completed with good score
  useEffect(() => {
    if (showCongrats) {
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }
        const particleCount = 50 * (timeLeft / duration);
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);
    }
  }, [showCongrats]);

  const slides = [
    // Slide 1 - Titre
    {
      id: 1,
      type: 'title',
      title: "Activité 10",
      subtitle: "Transmission des observations pour assurer la continuité des soins et des activités",
      description: "DEAP – Communication professionnelle et responsabilité"
    },
    // Slide 2 - Sommaire
    {
      id: 2,
      type: 'sommaire',
      title: "Sommaire",
      sections: [
        { num: "1", title: "Introduction : enjeux et cadre professionnel" },
        { num: "2", title: "Définition de la continuité des soins" },
        { num: "3", title: "Les différents types de transmissions" },
        { num: "4", title: "Les outils de transmission et de traçabilité" },
        { num: "5", title: "Les données à rechercher et à tracer" },
        { num: "6", title: "Les interlocuteurs" },
        { num: "7", title: "Le secret professionnel" },
        { num: "8", title: "Analyse professionnelle" },
        { num: "9", title: "Conclusion" }
      ]
    },
    // SECTION 1 - Introduction
    {
      id: 3,
      type: 'section',
      section: "1",
      title: "Introduction : enjeux et cadre professionnel"
    },
    {
      id: 4,
      type: 'content',
      title: "L'activité 10 dans le cadre du DEAP",
      content: "La transmission des observations vise à assurer la continuité des soins et des activités auprès de l'enfant. Elle ne consiste pas simplement à « dire ce qu'il s'est passé ».",
      subtitle: "Elle implique :",
      bullets: [
        "Une observation rigoureuse",
        "Une sélection pertinente des informations",
        "Une traçabilité adaptée",
        "Le respect du secret professionnel",
        "Une responsabilité partagée dans l'équipe"
      ],
      highlight: "L'auxiliaire de puériculture est un maillon essentiel de cette chaîne d'informations."
    },
    // SECTION 2 - Continuité des soins
    {
      id: 5,
      type: 'section',
      section: "2",
      title: "Qu'est-ce que la continuité des soins ?"
    },
    {
      id: 6,
      type: 'definition',
      title: "La continuité des soins signifie que l'enfant bénéficie :",
      items: [
        { icon: "🔄", text: "D'un accompagnement cohérent" },
        { icon: "🎯", text: "D'une prise en charge adaptée à ses besoins" },
        { icon: "🛡️", text: "D'une réponse sécurisée à tout moment" },
        { icon: "👨‍👩‍👧", text: "D'une coordination entre professionnels et famille" }
      ]
    },
    {
      id: 7,
      type: 'warning',
      title: "Éviter toute rupture d'information",
      content: "Une rupture d'information peut impacter :",
      bullets: [
        "La santé de l'enfant",
        "Sa sécurité",
        "Son bien-être",
        "Son développement"
      ],
      note: "La continuité des soins permet à chaque professionnel de prendre le relais sans perte d'informations essentielles.",
      warningColor: "#DC2626"
    },
    // SECTION 3 - Types de transmissions
    {
      id: 8,
      type: 'section',
      section: "3",
      title: "Les différents types de transmissions"
    },
    {
      id: 9,
      type: 'transmissionType',
      title: "A. Les transmissions orales",
      icon: "🗣️",
      subtitle: "Entre professionnels",
      content: "Elles ont lieu lors des relais d'équipe, en début et fin de poste, lors de réunions, en cas d'événement particulier.",
      items: [
        "L'état général de l'enfant",
        "Les incidents (chute, fièvre, morsure…)",
        "L'alimentation et le sommeil",
        "Le comportement inhabituel",
        "Les soins réalisés"
      ],
      qualities: ["Claires", "Factuelles", "Synthétiques", "Sans jugement"]
    },
    {
      id: 10,
      type: 'transmissionType',
      title: "Transmissions orales avec les parents",
      icon: "👨‍👩‍👧",
      subtitle: "Moments privilégiés",
      content: "Accueil du matin, départ du soir, rendez-vous spécifiques.",
      items: [
        "Le déroulement de la journée",
        "L'alimentation",
        "Le sommeil",
        "Les activités",
        "Les événements particuliers"
      ]
    },
    {
      id: 11,
      type: 'imageSlide',
      title: "B. Les transmissions écrites",
      subtitle: "Garantissent la traçabilité",
      image: "/images/transmission_ecrite.png",
      imageAlt: "Professionnel de santé écrivant des notes",
      bullets: [
        "Cahier de transmissions",
        "Fiche individuelle",
        "Dossier de l'enfant",
        "Fiche d'incident",
        "Logiciel professionnel"
      ]
    },
    {
      id: 12,
      type: 'rules',
      title: "Règles de l'écrit professionnel",
      content: "L'écrit doit être :",
      rules: [
        { icon: "📅", text: "Daté" },
        { icon: "✍️", text: "Signé" },
        { icon: "📋", text: "Objectif" },
        { icon: "👁️", text: "Lisible" },
        { icon: "⏰", text: "Chronologique" }
      ],
      highlight: "On écrit des faits observables, jamais des interprétations."
    },
    {
      id: 13,
      type: 'comparison',
      title: "Exemple d'écriture objective",
      bad: {
        label: "❌ À éviter",
        text: "« Enfant capricieux »"
      },
      good: {
        label: "✅ Correct",
        text: "« Refuse de s'asseoir, pleure 10 minutes avant le repas »"
      }
    },
    {
      id: 14,
      type: 'transmissionType',
      title: "C. Les transmissions ciblées",
      icon: "🎯",
      subtitle: "Inspirées du modèle IDE",
      content: "Elles structurent l'information autour de 3 éléments :",
      items: [
        "Un problème identifié (Cible)",
        "Une action réalisée",
        "Un résultat observé"
      ],
      example: {
        title: "Exemple : Hyperthermie",
        cible: "Hyperthermie",
        donnees: "Température 38,5°C",
        action: "Surveillance + information IDE",
        resultat: "Température redescendue à 37,8°C"
      }
    },
    {
      id: 15,
      type: 'content',
      title: "D. Les transmissions narratives",
      content: "Elles décrivent la journée de manière plus globale. Utilisées pour :",
      bullets: [
        "Le suivi du développement",
        "Les observations comportementales",
        "Les projets individualisés"
      ],
      note: "Elles sont plus descriptives et contextualisées."
    },
    // SECTION 4 - Outils
    {
      id: 16,
      type: 'section',
      section: "4",
      title: "Les outils de transmission et de traçabilité"
    },
    {
      id: 17,
      type: 'tools',
      title: "Les documents internes",
      categories: [
        {
          name: "Documents individuels",
          items: ["Dossier individuel de l'enfant", "Fiche de liaison", "Projet d'Accueil Individualisé (PAI)"]
        },
        {
          name: "Fiches de suivi",
          items: ["Fiche d'incident", "Fiche de suivi alimentaire", "Fiche de suivi du sommeil", "Fiche de soins"]
        }
      ]
    },
    {
      id: 18,
      type: 'highlight',
      title: "Le PAI - Projet d'Accueil Individualisé",
      content: "Il encadre les situations spécifiques :",
      items: [
        { icon: "🚫", text: "Allergies" },
        { icon: "💊", text: "Maladies chroniques" },
        { icon: "💉", text: "Traitements" }
      ],
      note: "Il impose une traçabilité rigoureuse des actes effectués."
    },
    {
      id: 19,
      type: 'imageSlide',
      title: "Les logiciels professionnels",
      subtitle: "Exemples : Meeko, Kidizz, Berceau des Rois",
      image: "/images/dossier_patient.png",
      imageAlt: "Dossier patient numérique",
      bullets: [
        "Transmission en temps réel",
        "Archivage sécurisé",
        "Communication avec les parents",
        "Suivi individualisé"
      ],
      advantages: ["Centralisation des données", "Sécurisation des accès", "Historique consultable"]
    },
    // SECTION 5 - Données
    {
      id: 20,
      type: 'section',
      section: "5",
      title: "Les données à rechercher et à tracer"
    },
    {
      id: 21,
      type: 'twoColumns',
      title: "Quelles informations transmettre ?",
      column1: {
        title: "✅ Je recherche et retransmets",
        items: [
          "L'état de santé",
          "Les modifications de comportement",
          "L'appétit et le sommeil",
          "Les interactions sociales",
          "Les incidents",
          "Les soins effectués",
          "Les consignes parentales"
        ]
      },
      column2: {
        title: "❌ Je ne retransmets jamais",
        items: [
          "Une opinion personnelle",
          "Une information non vérifiée",
          "Un jugement",
          "Des interprétations"
        ]
      }
    },
    // SECTION 6 - Interlocuteurs
    {
      id: 22,
      type: 'section',
      section: "6",
      title: "Les interlocuteurs"
    },
    {
      id: 23,
      type: 'interlocutors',
      title: "Avec qui je communique ?",
      groups: [
        {
          name: "Professionnels",
          icon: "👩‍⚕️",
          members: ["Auxiliaires de puériculture", "Éducateurs de jeunes enfants", "Infirmière", "Médecin", "Direction"]
        },
        {
          name: "Famille",
          icon: "👨‍👩‍👧",
          members: ["Parents", "Transmission adaptée au rôle et au cadre"]
        }
      ]
    },
    // SECTION 7 - Secret professionnel
    {
      id: 24,
      type: 'section',
      section: "7",
      title: "Le secret professionnel"
    },
    {
      id: 25,
      type: 'legal',
      title: "Cadre légal du secret professionnel",
      content: "Le secret professionnel est encadré par le Code pénal (article 226-13).",
      subtitle: "Il interdit la divulgation d'informations confidentielles concernant :",
      items: [
        "La santé de l'enfant",
        "La situation familiale",
        "Les difficultés sociales"
      ]
    },
    {
      id: 26,
      type: 'rules',
      title: "Le partage d'informations",
      content: "Le partage est possible uniquement :",
      rules: [
        { icon: "👥", text: "Entre professionnels concernés" },
        { icon: "🧒", text: "Dans l'intérêt de l'enfant" },
        { icon: "⚖️", text: "De manière proportionnée" }
      ]
    },
    {
      id: 27,
      type: 'warning',
      title: "Conséquences en cas de non-respect",
      content: "Le non-respect du secret professionnel peut entraîner :",
      bullets: [
        "Sanctions disciplinaires",
        "Sanctions pénales",
        "Perte de confiance des familles",
        "Mise en danger de l'enfant"
      ],
      warningColor: "#DC2626"
    },
    // SECTION 8 - Analyse
    {
      id: 28,
      type: 'section',
      section: "8",
      title: "Analyse professionnelle : choix des supports"
    },
    {
      id: 29,
      type: 'decisionTree',
      title: "Quel support choisir ?",
      decisions: [
        { situation: "Urgence", support: "Transmission orale", icon: "🚨" },
        { situation: "Traçabilité", support: "Transmission écrite", icon: "📝" },
        { situation: "Situation médicale", support: "Transmission ciblée", icon: "🎯" },
        { situation: "Centralisation", support: "Logiciel professionnel", icon: "💻" }
      ],
      note: "Le support dépend du degré d'urgence, du destinataire et de la nature de l'information."
    },
    // SECTION 9 - Conclusion
    {
      id: 30,
      type: 'section',
      section: "9",
      title: "Conclusion"
    },
    {
      id: 31,
      type: 'conclusion',
      title: "La transmission : un acte professionnel",
      content: "La transmission est :",
      bullets: [
        "Un acte professionnel engageant la responsabilité",
        "Une responsabilité éthique",
        "Un outil de sécurité",
        "Un garant de qualité"
      ],
      finalNote: "Elle ne se limite pas à informer : elle permet d'assurer la continuité, la cohérence et la sécurité de l'accompagnement de l'enfant."
    }
  ];

  const quizQuestions = [
    { id: 1, question: "Quel est l'objectif principal des transmissions ?", options: ["Informer la direction", "Assurer la continuité des soins", "Remplir un document obligatoire", "Évaluer les parents"], correct: 1 },
    { id: 2, question: "Une transmission orale doit être :", options: ["Rapide et subjective", "Détaillée et interprétative", "Claire, factuelle et synthétique", "Informelle"], correct: 2 },
    { id: 3, question: "La transmission écrite permet principalement :", options: ["De remplacer l'oral", "D'assurer une traçabilité", "De justifier son travail", "De communiquer avec tous les parents"], correct: 1 },
    { id: 4, question: "Une transmission ciblée comprend :", options: ["Une opinion personnelle", "Un résumé de la journée", "Problème – Action – Résultat", "Une discussion d'équipe"], correct: 2 },
    { id: 5, question: "Exemple d'écriture objective :", options: ["« Enfant difficile »", "« Enfant capricieux »", "« Refuse de s'asseoir, pleure 10 minutes »", "« Comportement insupportable »"], correct: 2 },
    { id: 6, question: "Le secret professionnel est encadré par :", options: ["Le règlement intérieur", "Le Code civil", "Le Code pénal", "Le contrat de travail"], correct: 2 },
    { id: 7, question: "Peut-on transmettre une information confidentielle ?", options: ["Oui, à tout collègue", "Oui, si c'est dans l'intérêt de l'enfant et entre professionnels concernés", "Oui, aux autres parents", "Non, jamais"], correct: 1 },
    { id: 8, question: "Quel outil est adapté au suivi d'un enfant allergique ?", options: ["Cahier de présence", "Planning hebdomadaire", "Projet d'Accueil Individualisé", "Tableau d'affichage"], correct: 2 },
    { id: 9, question: "Lors d'une hyperthermie, il est important de transmettre :", options: ["L'avis personnel", "La température exacte", "Une hypothèse", "Rien si elle baisse"], correct: 1 },
    { id: 10, question: "Le logiciel professionnel permet :", options: ["De remplacer toute transmission orale", "D'archiver et sécuriser les données", "De supprimer les dossiers papier", "De transmettre aux familles des informations confidentielles"], correct: 1 },
    { id: 11, question: "Une transmission doit être signée et datée pour :", options: ["Faire joli", "Identifier le professionnel responsable", "Faire plaisir à la direction", "Éviter d'écrire"], correct: 1 },
    { id: 12, question: "Quelle information est pertinente pour la continuité des soins ?", options: ["Température du frigo", "Changement d'appétit", "Absence d'un collègue", "Décoration de la salle"], correct: 1 },
    { id: 13, question: "Une transmission narrative est utile pour :", options: ["Les observations de développement", "Les urgences vitales", "Les factures", "Les horaires du personnel"], correct: 0 },
    { id: 14, question: "Lors d'un relais d'équipe, la transmission orale permet :", options: ["De critiquer la prise en charge précédente", "D'assurer un passage de relais sécurisé", "D'éviter l'écrit", "De raccourcir la journée"], correct: 1 },
    { id: 15, question: "Que ne faut-il jamais écrire ?", options: ["Une heure précise", "Un soin réalisé", "Une interprétation personnelle", "Une température"], correct: 2 },
    { id: 16, question: "En cas de non-respect du secret professionnel, le professionnel risque :", options: ["Rien", "Une sanction pénale", "Une récompense", "Une mutation automatique"], correct: 1 },
    { id: 17, question: "La continuité des soins signifie :", options: ["Que le même professionnel s'occupe toujours de l'enfant", "Qu'il n'y ait aucune transmission", "Que l'accompagnement reste cohérent malgré les relais", "Que les parents ne soient pas informés"], correct: 2 },
    { id: 18, question: "Une chute sans gravité doit-elle être transmise ?", options: ["Non", "Oui, systématiquement", "Seulement si les parents demandent", "Non si l'enfant ne pleure pas"], correct: 1 },
    { id: 19, question: "Le choix du support dépend :", options: ["De l'humeur", "De la gravité et du destinataire", "Du temps disponible", "De la météo"], correct: 1 },
    { id: 20, question: "La transmission est :", options: ["Une formalité administrative", "Un acte professionnel engageant la responsabilité", "Facultative", "Réservée à la direction"], correct: 1 }
  ];

  const API_URL = window.location.origin || '';

  const transformTextForTTS = (text) => {
    if (!text) return '';
    let transformed = text
      .replace(/\bAP\b/g, 'auxiliaire de puériculture')
      .replace(/\bDEAP\b/g, 'diplôme d\'état d\'auxiliaire de puériculture')
      .replace(/\bIDE\b/g, 'infirmière diplômée d\'état')
      .replace(/\bPAI\b/g, 'projet d\'accueil individualisé')
      .replace(/\bEJE\b/g, 'éducateur de jeunes enfants');
    return transformed;
  };

  const speakText = async (text) => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const transformedText = transformTextForTTS(text);
      const truncatedText = transformedText.substring(0, 800);
      
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: truncatedText, voice: 'nova', speed: 1.1 })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.audio_url) {
          if (audioRef.current) {
            audioRef.current.pause();
          }
          audioRef.current = new Audio(data.audio_url);
          audioRef.current.play();
          setIsPlaying(true);
          audioRef.current.onended = () => setIsPlaying(false);
        } else if (data.audio_base64) {
          if (audioRef.current) {
            audioRef.current.pause();
          }
          audioRef.current = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          audioRef.current.play();
          setIsPlaying(true);
          audioRef.current.onended = () => setIsPlaying(false);
        }
      } else {
        toast.error("Erreur lors de la génération audio");
      }
    } catch (error) {
      toast.error("Erreur de connexion au service audio");
    } finally {
      setIsLoading(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const getSlideText = (slide) => {
    let text = slide.title || '';
    if (slide.subtitle) text += '. ' + slide.subtitle;
    if (slide.content) text += '. ' + slide.content;
    if (slide.bullets) text += '. ' + slide.bullets.join('. ');
    if (slide.note) text += '. ' + slide.note;
    if (slide.highlight) text += '. ' + slide.highlight;
    if (slide.items) text += '. ' + slide.items.map(i => i.text || i).join('. ');
    return text;
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      const newSlide = currentSlide + 1;
      setCurrentSlide(newSlide);
      markSlideViewed(ACTIVITY_ID, newSlide, slides.length);
    } else {
      setShowQuiz(true);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    markSlideViewed(ACTIVITY_ID, index, slides.length);
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    if (!quizSubmitted) {
      setQuizAnswers({ ...quizAnswers, [questionId]: answerIndex });
    }
  };

  const submitQuiz = () => {
    let score = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) score++;
    });
    setQuizScore(score);
    setQuizSubmitted(true);
    markQuizCompleted(ACTIVITY_ID, score, quizQuestions.length);
    
    if (score >= 16) {
      setShowCongrats(true);
    }
    
    toast.success(`Quiz terminé ! Score: ${score}/${quizQuestions.length}`);
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center p-8" style={{ background: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)' }}>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">{slide.title}</h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl">{slide.subtitle}</p>
            <p className="text-lg text-white/80 mt-4">{slide.description}</p>
          </div>
        );

      case 'sommaire':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-4 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-w-4xl mx-auto">
              {slide.sections.map((section, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-2 p-2 bg-white rounded-lg shadow-sm border border-cyan-100 cursor-pointer hover:bg-cyan-50 transition-colors"
                  onClick={() => goToSlide(Math.min(idx * 3 + 2, slides.length - 1))}
                >
                  <span className="w-6 h-6 bg-cyan-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {section.num}
                  </span>
                  <span className="text-gray-700 text-xs">{section.title}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full" style={{ background: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)' }}>
            <div className="text-6xl font-bold text-white/30 mb-4">{slide.section}</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center px-8">{slide.title}</h2>
          </div>
        );

      case 'content':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-4">{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            {slide.subtitle && <p className="text-cyan-700 font-medium mb-3">{slide.subtitle}</p>}
            {slide.bullets && (
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
            {slide.highlight && (
              <div className="mt-4 p-4 bg-cyan-100 border-l-4 border-cyan-500 rounded">
                <p className="text-cyan-800 font-medium">{slide.highlight}</p>
              </div>
            )}
            {slide.note && <p className="mt-4 text-sm text-cyan-600 italic">{slide.note}</p>}
          </div>
        );

      case 'definition':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-6">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {slide.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm border border-cyan-100">
                  <span className="text-3xl">{item.icon}</span>
                  <span className="text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'warning':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: `linear-gradient(180deg, ${slide.warningColor}10 0%, #FFFFFF 100%)` }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: slide.warningColor }}>{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            {slide.bullets && (
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <XCircle size={18} style={{ color: slide.warningColor }} className="flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
            {slide.note && (
              <div className="mt-4 p-4 bg-cyan-50 rounded-lg">
                <p className="text-cyan-700">{slide.note}</p>
              </div>
            )}
          </div>
        );

      case 'transmissionType':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">{slide.icon}</span>
              <h2 className="text-2xl font-bold text-cyan-800">{slide.title}</h2>
            </div>
            {slide.subtitle && <p className="text-cyan-600 font-medium mb-2">{slide.subtitle}</p>}
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            {slide.items && (
              <ul className="space-y-2 mb-4">
                {slide.items.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-cyan-500" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {slide.qualities && (
              <div className="flex flex-wrap gap-2 mt-4">
                {slide.qualities.map((q, idx) => (
                  <span key={idx} className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium">
                    {q}
                  </span>
                ))}
              </div>
            )}
            {slide.example && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="font-bold text-amber-800 mb-2">{slide.example.title}</p>
                <div className="space-y-1 text-sm">
                  <p><strong>Cible :</strong> {slide.example.cible}</p>
                  <p><strong>Données :</strong> {slide.example.donnees}</p>
                  <p><strong>Action :</strong> {slide.example.action}</p>
                  <p><strong>Résultat :</strong> {slide.example.resultat}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'imageSlide':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-2">{slide.title}</h2>
            {slide.subtitle && <p className="text-cyan-600 mb-3">{slide.subtitle}</p>}
            <div className="flex flex-col md:flex-row gap-4 items-start">
              <div className="md:w-1/2">
                <img 
                  src={slide.image} 
                  alt={slide.imageAlt}
                  className="w-full max-h-56 object-contain rounded-lg shadow-md bg-white p-2"
                />
              </div>
              <div className="md:w-1/2">
                {slide.bullets && (
                  <ul className="space-y-2">
                    {slide.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle size={16} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {slide.advantages && (
                  <div className="mt-3 p-3 bg-green-50 rounded-lg">
                    <p className="font-medium text-green-800 mb-2">Avantages :</p>
                    <ul className="space-y-1">
                      {slide.advantages.map((adv, idx) => (
                        <li key={idx} className="text-green-700 text-sm flex items-center gap-2">
                          <Sparkles size={14} /> {adv}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'rules':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-4">{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {slide.rules.map((rule, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-cyan-100">
                  <span className="text-2xl">{rule.icon}</span>
                  <span className="text-gray-700 font-medium">{rule.text}</span>
                </div>
              ))}
            </div>
            {slide.highlight && (
              <div className="mt-4 p-4 bg-amber-100 border-l-4 border-amber-500 rounded">
                <p className="text-amber-800 font-medium">{slide.highlight}</p>
              </div>
            )}
          </div>
        );

      case 'comparison':
        return (
          <div className="p-8 h-full overflow-auto flex flex-col justify-center" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-6 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <div className="p-6 bg-red-50 rounded-lg border-2 border-red-200">
                <p className="font-bold text-red-700 mb-3">{slide.bad.label}</p>
                <p className="text-red-600 text-lg italic">"{slide.bad.text}"</p>
              </div>
              <div className="p-6 bg-green-50 rounded-lg border-2 border-green-200">
                <p className="font-bold text-green-700 mb-3">{slide.good.label}</p>
                <p className="text-green-600 text-lg italic">"{slide.good.text}"</p>
              </div>
            </div>
          </div>
        );

      case 'tools':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-6">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {slide.categories.map((cat, idx) => (
                <div key={idx} className="bg-white rounded-lg p-4 shadow-sm border border-cyan-100">
                  <h3 className="font-bold text-cyan-700 mb-3 flex items-center gap-2">
                    <FileText size={18} /> {cat.name}
                  </h3>
                  <ul className="space-y-2">
                    {cat.items.map((item, i) => (
                      <li key={i} className="text-gray-600 text-sm flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'highlight':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-4">{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-6">{slide.content}</p>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {slide.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm border border-cyan-100">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>
            {slide.note && (
              <p className="mt-4 text-sm text-cyan-600 italic bg-cyan-50 p-3 rounded">{slide.note}</p>
            )}
          </div>
        );

      case 'twoColumns':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-6 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="font-bold text-green-700 mb-3">{slide.column1.title}</h3>
                <ul className="space-y-2">
                  {slide.column1.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-green-700 text-sm">
                      <CheckCircle size={14} /> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <h3 className="font-bold text-red-700 mb-3">{slide.column2.title}</h3>
                <ul className="space-y-2">
                  {slide.column2.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-red-700 text-sm">
                      <XCircle size={14} /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case 'interlocutors':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-6 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {slide.groups.map((group, idx) => (
                <div key={idx} className="bg-white rounded-lg p-4 shadow-sm border border-cyan-100">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-3xl">{group.icon}</span>
                    <h3 className="font-bold text-cyan-700">{group.name}</h3>
                  </div>
                  <ul className="space-y-2">
                    {group.members.map((member, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600">
                        <Users size={14} className="text-cyan-400" /> {member}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'legal':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <div className="flex items-center gap-3 mb-4">
              <Shield size={28} className="text-cyan-600" />
              <h2 className="text-2xl font-bold text-cyan-800">{slide.title}</h2>
            </div>
            <div className="p-4 bg-cyan-100 rounded-lg mb-4">
              <p className="text-cyan-800">{slide.content}</p>
            </div>
            {slide.subtitle && <p className="text-gray-700 font-medium mb-3">{slide.subtitle}</p>}
            {slide.items && (
              <ul className="space-y-2">
                {slide.items.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );

      case 'decisionTree':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-cyan-800 mb-6 text-center">{slide.title}</h2>
            <div className="space-y-3 max-w-2xl mx-auto">
              {slide.decisions.map((dec, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm border border-cyan-100">
                  <span className="text-2xl">{dec.icon}</span>
                  <div className="flex-1">
                    <span className="text-gray-600">{dec.situation}</span>
                  </div>
                  <div className="text-right">
                    <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium">
                      {dec.support}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {slide.note && (
              <p className="mt-4 text-sm text-cyan-600 italic text-center">{slide.note}</p>
            )}
          </div>
        );

      case 'conclusion':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)' }}>
            <h2 className="text-3xl font-bold text-white mb-6 text-center">{slide.title}</h2>
            <p className="text-white/90 mb-4 text-center">{slide.content}</p>
            <div className="max-w-2xl mx-auto">
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-white">
                    <CheckCircle size={20} className="text-cyan-200" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 p-4 bg-white/20 rounded-lg max-w-2xl mx-auto">
              <p className="text-white text-center">{slide.finalNote}</p>
            </div>
          </div>
        );

      default:
        return (
          <div className="p-8 h-full flex items-center justify-center">
            <p className="text-gray-500">Contenu non disponible</p>
          </div>
        );
    }
  };

  // Congratulations screen
  if (showCongrats) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-cyan-600 via-blue-600 to-purple-600 flex items-center justify-center z-50">
        <div className="text-center p-8 max-w-2xl">
          <div className="mb-6">
            <Award size={80} className="mx-auto text-yellow-400 animate-bounce" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            🎉 Félicitations ! 🎉
          </h1>
          <p className="text-2xl text-white/90 mb-4">
            Vous avez terminé l'Activité 10 avec brio !
          </p>
          <div className="bg-white/20 rounded-xl p-6 mb-6">
            <p className="text-3xl font-bold text-white mb-2">
              Score : {quizScore}/{quizQuestions.length}
            </p>
            <p className="text-white/80">
              {quizScore === 20 ? "Score parfait ! Vous êtes un(e) expert(e) de la transmission !" :
               quizScore >= 18 ? "Excellent ! Maîtrise quasi-parfaite !" :
               "Très bon travail ! Vous maîtrisez les transmissions."}
            </p>
          </div>
          <p className="text-lg text-white/80 mb-6">
            Vous êtes maintenant prêt(e) à assurer la continuité des soins avec professionnalisme !
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => { setShowCongrats(false); setShowQuiz(false); setCurrentSlide(0); }}
              className="px-6 py-3 bg-white text-cyan-600 rounded-lg font-medium hover:bg-cyan-50 transition-colors"
            >
              Revoir le cours
            </button>
            <button
              onClick={() => setShowCongrats(false)}
              className="px-6 py-3 bg-cyan-500 text-white rounded-lg font-medium hover:bg-cyan-400 transition-colors"
            >
              Voir mes réponses
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showQuiz) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-2xl font-bold text-cyan-800 mb-6">Quiz - Activité 10</h2>
        <p className="text-gray-600 mb-6">Testez vos connaissances sur la transmission des observations.</p>
        
        <div className="space-y-6">
          {quizQuestions.map((q, idx) => (
            <div key={q.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
              <p className="font-medium text-gray-800 mb-3">{idx + 1}. {q.question}</p>
              <div className="space-y-2">
                {q.options.map((option, optIdx) => (
                  <button
                    key={optIdx}
                    onClick={() => handleQuizAnswer(q.id, optIdx)}
                    disabled={quizSubmitted}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      quizSubmitted
                        ? optIdx === q.correct
                          ? 'bg-green-100 border-green-500'
                          : quizAnswers[q.id] === optIdx
                            ? 'bg-red-100 border-red-500'
                            : 'bg-gray-50 border-gray-200'
                        : quizAnswers[q.id] === optIdx
                          ? 'bg-cyan-100 border-cyan-500'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {quizSubmitted && optIdx === q.correct && <CheckCircle size={18} className="text-green-600" />}
                      {quizSubmitted && quizAnswers[q.id] === optIdx && optIdx !== q.correct && <XCircle size={18} className="text-red-600" />}
                      {option}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {!quizSubmitted ? (
          <button
            onClick={submitQuiz}
            disabled={Object.keys(quizAnswers).length < quizQuestions.length}
            className="mt-6 w-full py-3 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Valider le quiz ({Object.keys(quizAnswers).length}/{quizQuestions.length} réponses)
          </button>
        ) : (
          <div className="mt-6 p-6 bg-cyan-50 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-cyan-800">Score : {quizScore}/{quizQuestions.length}</h3>
            <p className="text-cyan-600 mt-2">
              {quizScore >= 16 ? "Excellent ! Vous maîtrisez le sujet." :
               quizScore >= 12 ? "Bon travail ! Quelques points à revoir." :
               "Continuez à étudier pour améliorer vos connaissances."}
            </p>
            <button
              onClick={() => { setShowQuiz(false); setCurrentSlide(0); setQuizAnswers({}); setQuizSubmitted(false); }}
              className="mt-4 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Revoir la présentation
            </button>
          </div>
        )}
      </div>
    );
  }

  const slide = slides[currentSlide];

  return (
    <div className="flex flex-col h-full">
      {/* Progress bar */}
      <div className="h-1 bg-gray-200">
        <div 
          className="h-full bg-cyan-500 transition-all duration-300"
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>

      {/* Slide content */}
      <div className="flex-1 overflow-hidden">
        {renderSlide(slide)}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between p-4 bg-white border-t">
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={20} />
          Précédent
        </button>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-100 text-cyan-700 rounded-lg hover:bg-cyan-200 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Chargement...
              </>
            ) : isPlaying ? (
              <>
                <VolumeX size={18} />
                Arrêter
              </>
            ) : (
              <>
                <Volume2 size={18} />
                Écouter
              </>
            )}
          </button>
        </div>

        <button
          onClick={nextSlide}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
        >
          {currentSlide === slides.length - 1 ? 'Quiz' : 'Suivant'}
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default DEAPActivite10Presentation;
